/* eslint-disable react-refresh/only-export-components */
// import { BASE_URL } from "./types";
// import type { User as FirebaseUser } from "firebase/auth";
import ApiCall from "./ApiCall";
import { BaseCall } from "./BaseCall";
//import { auth } from "./firebase";
//import { useNavigate } from "react-router-dom";
import type { User } from "./types";
import type { NavigateFunction } from "react-router-dom";

// Types
// interface AuthResult {
//   isNewUser: boolean;
//   user: FirebaseUser | null;
// }

// Silently refreshes subscription/profile data in localStorage without navigating.
// Call on app load to ensure cached expiration dates stay in sync with the backend.
export async function refreshProfileCache(): Promise<void> {
  try {
    const response = await ApiCall.makeAuthenticatedRequest(
      "/accounts/api/user/profile/",
    );
    if (response.error) return;

    const profile_data = response as {
      state_id: number;
      subscription_id?: string;
      subscription_description?: string;
      pending_subscription_id?: string;
      pending_subscription_description?: string;
      subscription_expiration?: string;
      access_level?: number;
      has_discount?: boolean;
    };

    localStorage.setItem("state_id", String(profile_data.state_id));
    localStorage.setItem("subscription_id", profile_data.subscription_id || "");
    localStorage.setItem(
      "subscription_name",
      profile_data.subscription_description || "",
    );
    localStorage.setItem(
      "pending_subscription_id",
      profile_data.pending_subscription_id || "",
    );
    localStorage.setItem(
      "pending_subscription_name",
      profile_data.pending_subscription_description || "",
    );
    localStorage.setItem(
      "subscription_expiration",
      profile_data.subscription_expiration || "",
    );
    localStorage.setItem(
      "access_level",
      String(profile_data.access_level || ""),
    );
    localStorage.setItem(
      "has_discount",
      profile_data.has_discount ? "true" : "false",
    );
  } catch {
    // Silently ignore — stale cache is better than a crash on load
  }
}

export async function SetUserProfile(data: Record<string, unknown>) {
  try {
    const response = await ApiCall.makeAuthenticatedRequest(
      "/accounts/api/user/profile/create/",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return {
      profile_save_output: response.data,
      profile_saving: false,
      profile_save_error: response.error || null,
    };
  } catch (error: unknown) {
    return {
      profile_save_output: null,
      profile_saving: false,
      profile_save_error: (error as Error).message,
    };
  }
}

export async function GetUserProfile() {
  try {
    const response = await ApiCall.makeAuthenticatedRequest(
      "/accounts/api/user/profile/",
    );
    return response.data;
    // return {
    //   profile_data: response.data,
    //   profile_loading: false,
    //   profile_load_error: response.error || null,
    // };
  } catch (error: unknown) {
    return {
      profile_data: null,
      profile_loading: false,
      profile_load_error: (error as Error).message,
    };
  }
}

export function GetUserAvatar() {
  const { output, loading, error } = BaseCall("/accounts/api/avatar/");
  return {
    avatar_data: output,
    pavatar_loading: loading,
    avatar_load_error: error,
  };
}

// Returns null if user has Pro access, or "/games" if they don't
export function ProAccessCheck(): string | null {
  const stateId = localStorage.getItem("state_id");
  if (stateId === "7" || stateId === "8") return null;
  const subscriptionName = (
    localStorage.getItem("subscription_name") || ""
  ).toLowerCase();
  if (subscriptionName.includes("pro") || subscriptionName.includes("premium"))
    return null;
  return "/games";
}

// Returns null if user has Premium access, or "/games" if they don't
export function PremiumAccessCheck(): string | null {
  const stateId = localStorage.getItem("state_id");
  if (stateId === "7" || stateId === "8") return null;
  const subscriptionName = (
    localStorage.getItem("subscription_name") || ""
  ).toLowerCase();
  if (subscriptionName.includes("premium")) return null;
  return "/games";
}

// Returns "/rules" if a Basic plan user has more than 5 rules, otherwise null
export function RuleLimitCheck(): string | null {
  const subscriptionName = (
    localStorage.getItem("subscription_name") || ""
  ).toLowerCase();
  if (!subscriptionName.includes("basic")) return null;
  const ruleCount = parseInt(localStorage.getItem("rule_count") || "0", 10);
  return ruleCount > 5 ? "/rules" : null;
}

// Returns null if user has access, or a redirect path if they don't
export function AccessVerification(): string | null {
  const state_id = localStorage.getItem("state_id");
  const expiration = localStorage.getItem("subscription_expiration");

  // Admin/test users always have access
  if (state_id === "7" || state_id === "8") {
    return null;
  }

  // State 1 = needs to subscribe
  if (state_id === "1") {
    return "/subscribe";
  }

  // State 4 = expired subscription still within billing period — allow access until expiration
  // State 5 = cancelled trial still within trial period — allow access until expiration
  // State 6 = cancelled subscription still within billing period — allow access until expiration
  if (state_id === "4" || state_id === "5" || state_id === "6") {
    if (expiration && new Date(expiration) > new Date()) {
      return null;
    }
    return "/settings";
  }

  // Only state_id 2 or 3 are valid active subscription states
  if (state_id !== "2" && state_id !== "3") {
    return "/settings";
  }

  // Check if subscription has expired
  if (expiration) {
    const expirationDate = new Date(expiration);
    const now = new Date();

    if (expirationDate < now) {
      // state 2 = trial → expires to 5 (expired trial)
      // state 3 = active subscription → expires to 4 (expired subscription)
      if (state_id === "2") {
        localStorage.setItem("state_id", "5");
      } else if (state_id === "3") {
        localStorage.setItem("state_id", "4");
      }
      return "/settings";
    }
  }

  return null;
}

export async function getUserProfileSettings(
  user: User,
  provider: string,
  username: string = "",
  navigate: NavigateFunction,
  inviteCode?: string,
): Promise<void> {
  if (user.isNewUser) {
    const data: Record<string, string | number> = {
      firebase_id: user.id || "",
      firebase_created_at: new Date(user.creationTime || 0).getTime(),
      last_sign_in: new Date(user.lastSignInTime || 0).getTime(),
      provider: provider,
      avatar: user.avatar ? user.avatar : "",
    };
    if (username.length > 0) {
      data["first_name"] = username;
    }
    if (inviteCode) {
      data["invite_code"] = inviteCode;
    }
    const createResponse = await ApiCall.makeAuthenticatedRequest(
      "/accounts/api/user/profile/create/",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );

    if (createResponse.error) {
      throw new Error(String(createResponse.error));
    }

    // Send verification email after profile exists (email provider only)
    if (provider === "email") {
      await ApiCall.makeAuthenticatedRequest(
        "/accounts/api/email/send-verification/",
        { method: "POST" },
      );
    }
  }

  try {
    await cacheProfileSetting(navigate);
  } catch {
    //    console.error("Exception in getUserProfileSettings:", error);
  }
}

export async function cacheProfileSetting(
  navigate: NavigateFunction,
  overrideNavigationPath?: string,
) {
  const response = await ApiCall.makeAuthenticatedRequest(
    "/accounts/api/user/profile/",
  );

  // Type assertion for the response data
  const profile_data = response as {
    first_name: string;
    email: string;
    payment_type: string;
    avatar: string;
    provider: string;
    state_id: number;
    subscription_id?: string;
    subscription_description?: string;
    pending_subscription_id?: string;
    pending_subscription_description?: string;
    subscription_expiration?: string;
    access_level?: number;
    user_id: number;
    landing_page: string;
    just_subscribed: boolean;
    has_discount?: boolean;
  };

  localStorage.setItem("username", profile_data.first_name);
  localStorage.setItem("email", profile_data.email);
  localStorage.setItem("payment_type", profile_data.payment_type);
  localStorage.setItem("avatar_url", profile_data.avatar);
  localStorage.setItem("provider", profile_data.provider);
  localStorage.setItem("state_id", String(profile_data.state_id));
  localStorage.setItem("subscription_id", profile_data.subscription_id || "");
  localStorage.setItem(
    "subscription_name",
    profile_data.subscription_description || "",
  );
  localStorage.setItem(
    "pending_subscription_id",
    profile_data.pending_subscription_id || "",
  );
  localStorage.setItem(
    "pending_subscription_name",
    profile_data.pending_subscription_description || "",
  );
  localStorage.setItem("new_user", String(profile_data.just_subscribed));
  localStorage.setItem(
    "subscription_expiration",
    profile_data.subscription_expiration || "",
  );
  localStorage.setItem("access_level", String(profile_data.access_level || ""));
  localStorage.setItem(
    "has_discount",
    profile_data.has_discount ? "true" : "false",
  );
  localStorage.setItem("avatar_url", profile_data.avatar);
  localStorage.setItem("user_id", String(profile_data.user_id));
  localStorage.setItem("landing_page", profile_data.landing_page);

  // Use override path if provided, otherwise use landing_page logic
  if (overrideNavigationPath) {
    navigate(overrideNavigationPath);
  } else if (profile_data.landing_page === "Signup") {
    navigate("/tour");
  } else if (profile_data.landing_page === "Profile") {
    navigate("/settings");
  } else if (profile_data.landing_page === "Games") {
    navigate("/games");
  } else {
    // Default to games page if landing_page is not recognized
    navigate("/games");
  }
}
